import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
    ConfigConstants,
    CorporateInformationComponent,
    MenuEntryModel,
    MenuSectionModel,
    ReportIssueComponent,
    ReportIssueModel,
    SideMenuInterface,
    SideMenuService,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { getFormEscalationEmail } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AppState } from '../../store/state/state';

@Injectable({
    providedIn: 'root',
})
export class PaceSideMenuService implements SideMenuInterface {
    public isInitialized = false;
    public escalationEmail$: Observable<string>;
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        private readonly sideMenuService: SideMenuService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        private readonly store: Store<AppState>,
        private readonly router: Router,
    ) {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((navEnd: NavigationEnd) => {
                const isFormDetailsPage = navEnd.urlAfterRedirects.includes(ConfigConstants.routeUrlForms2 + '/');
                const menuSection = new MenuSectionModel('');
                const escalationMessageEntry = new MenuEntryModel(
                    <string>this.translate.instant('SIDE_MENU_BUTTON_ESCALATION_MESSAGE'),
                    () => this.openMailToEscalation(),
                );

                if (isFormDetailsPage) {
                    this.sideMenuService.displayEntry(menuSection.title, escalationMessageEntry.title);
                } else {
                    this.sideMenuService.hideEntry(menuSection.title, escalationMessageEntry.title);
                }
            });

        this.escalationEmail$ = this.store.select(getFormEscalationEmail);
    }

    public init(): Observable<void> {
        const menuSection = new MenuSectionModel('');
        const escalationMessageEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDE_MENU_BUTTON_ESCALATION_MESSAGE'),
            () => this.openMailToEscalation(),
        );

        const corporateInformationEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDE_MENU_BUTTON_CORPORATE_INFORMATION'),
            () => this.openCorporateInfo(),
        );

        const reportIssueEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDE_MENU_BUTTON_REPORT_ISSUE'),
            () => this.openReportIssue(),
        );

        menuSection.entries.push(escalationMessageEntry, corporateInformationEntry, reportIssueEntry);
        this.sideMenuService.addSection(menuSection);
        this.isInitialized = true;

        return of(undefined);
    }

    private openMailToEscalation(): void {
        this.escalationEmail$.pipe(take(1)).subscribe((escalationEmail) => {
            const email = escalationEmail.length > 0 ? escalationEmail : this.configService.getEscalationEmail();
            window.location.href = `mailto:${email}`;
        });
    }

    private openCorporateInfo(): void {
        this.modalService.show(CorporateInformationComponent, {
            backdrop: 'static',
            class: 'modal-md',
            ariaLabelledBy: 'corporate-information-modal-title',
        });
    }

    private openReportIssue(): void {
        this.modalService.show(ReportIssueComponent, {
            backdrop: 'static',
            class: 'modal-lg modal-fullscreen-lg-down',
            initialState: {
                model: new ReportIssueModel(),
            },
            ariaLabelledBy: 'report-issue-modal-title',
        });
    }
}
